import { useCallback, useEffect, useRef, useState } from "react";
import { trackEvent } from "@lib/analytics";
import { useUser } from "@hooks/useUser";
import { EXIT_ANIMATION_DURATION } from "./constants";
import {
    OnboardingStepAnalyticsEventMapping,
    OnboardingStepKey,
} from "./steps";

export function useManageSteps<T extends OnboardingStepKey>(
    isLoaded: boolean,
    getInitialStep: () => T,
) {
    const user = useUser();
    const [forcedStep, setForcedStep] = useState<T | undefined>();
    const [nextStep, setNextStep] = useState<T | undefined>();

    const [isExiting, setIsExiting] = useState(false);
    const timerRef = useRef<number | undefined>(undefined);

    const currentStep = forcedStep ?? getInitialStep();

    const go = useCallback(
        (stepKey: T) => {
            if (stepKey === currentStep) {
                return;
            }

            setNextStep(stepKey);
            setIsExiting(true);

            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            timerRef.current = window.setTimeout(() => {
                window.scroll({ top: 0, behavior: "instant" });
                setForcedStep(stepKey);
                setNextStep(undefined);
                setIsExiting(false);
            }, EXIT_ANIMATION_DURATION);

            const eventToTrack = OnboardingStepAnalyticsEventMapping[stepKey];
            if (eventToTrack && user) {
                void trackEvent(eventToTrack, {
                    name: user.name,
                    user_id: user.id,
                    step_key: stepKey,
                });
            }
        },
        [currentStep, user],
    );

    useEffect(() => {
        if (isLoaded) {
            setForcedStep(currentStep);
        }
    }, [currentStep, isLoaded]);

    return {
        isExiting,
        currentStep,
        nextStep,
        go,
    };
}
